/**
 * Modal for uploading a CSV file for Astera oncology submission.
 * Mirrors the Stat File Upload design without the template download option.
 */

import { useState } from "react";
import { Modal, UploadedFileBanner } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { DocUploadIcon } from "../../../svg/doc-upload-icon";
import { useFileUpload } from "../CaseInitiationUpload/hooks/useFileUpload";
import { onSaveOncologyFile } from "./actionHandlers/onSaveOncologyFile";

interface OncologyFileUploadModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Renders a modal with a file-naming info banner and drag-and-drop CSV upload
 * for Astera oncology file processing.
 *
 * @param props.onClose - Callback invoked when the modal is dismissed
 * @param props.onSuccess - Optional callback invoked after a successful upload
 */
const OncologyFileUploadModal = (props: OncologyFileUploadModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    uploadedFile,
    fileInputRef,
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleDeleteFile,
  } = useFileUpload();

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await onSaveOncologyFile(uploadedFile, () => {
        props.onSuccess?.();
        props.onClose();
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      dialogId={ModalId.ONCOLOGY_FILE_UPLOAD_MODAL}
      title="File Upload"
      onClose={props.onClose}
      onSave={handleSave}
      saveButtonText={isSubmitting ? "Running..." : "Run"}
      cancelText="Cancel"
      disableSave={isSubmitting || !uploadedFile}
    >
      <div className="flex flex-col gap-4">
      
        <div className="drag-drop-container">
          {!uploadedFile && (
            <div
              className="drop-file-input"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <DocUploadIcon />
              <div className="main-text">
                Drag and drop or <span>click here</span> to upload
              </div>
              <div className="sub-text">Supported file format CSV</div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                id="oncology-file-upload"
                name="oncology-file-upload"
              />
            </div>
          )}
        </div>

        {uploadedFile && (
          <div className="uploaded-file-container">
            <UploadedFileBanner
              fileName={uploadedFile.name}
              deleteUploadedFile={handleDeleteFile}
              currentProgress={100}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default OncologyFileUploadModal;
